import clsx from 'clsx';
import PageTitle from 'components/PageTitle';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import CarouselButtons from 'components/carousel2025/CarouselButtons';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import MainLayout from 'components/layout/MainLayout';
import useEmblaCarousel from 'embla-carousel-react';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { useScrollToElement } from 'hooks/useScrollToElement';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ASSET_PREFIX } from 'utils/constants';
import {
  fetchWinnersPinned,
  fetchWinnersSpotlight,
  fetchWinnersWallOfFame,
  WinnerCmsItem,
} from 'utils/plasmicCMS';
import styles from './winners.module.css';

interface Props {
  pinned: WinnerCmsItem[];
  spotlight: WinnerCmsItem[];
  wallOfFame: WinnerCmsItem[];
}

const Winners = (props: Props) => {
  const userStore = useUserStore();
  const uiStore = useUIStore();
  const isSubscriber = userStore.activeSubscriptions.length > 0;
  const wallOfFameCarouselInfo = useEmblaCarousel({
    loop: true,
    duration: 10,
    inViewThreshold: 0.1,
    slidesToScroll: 2,
  });

  const openModalVideo = (x: WinnerCmsItem) => {
    if (x.videoLinkInstagram) {
      uiStore.setVideoOverlayType('instagram');
      uiStore.setVideoOverlayLink(x.videoLinkInstagram);
      uiStore.setShowVideoOverlay(true);
    } else if (x.videoLink) {
      uiStore.setVideoOverlayType('youtube');
      uiStore.setVideoOverlayLink(x.videoLink);
      uiStore.setShowVideoOverlay(true);
    }
  };

  return (
    <MainLayout limitItemWidths>
      <NextSeo
        title="Winners | Win Epic Prizes | DAYMADE"
        description="Check out our winners. Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95"
        openGraph={{
          title: 'Winners | Win Epic Prizes | DAYMADE',
          description:
            'Check out our winners. Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95',
        }}
      />

      <div className="daymadeTypographyReset">
        <div className="withReducedSpacing">
          <PageTitle
            heroBackgroundImage={
              ASSET_PREFIX + '/images/heading/WinnerWithTrophy.svg'
            }
            rebrandDm2025
          >
            Winners
          </PageTitle>

          <AboutUsModule background="white" withReducedPadding>
            <AboutUsSiteContainer>
              <div className={styles.introArea}>
                <h2 className="aboutUsTypographyHeaderMedium">
                  Thousands of winners every week
                </h2>
                <div className={styles.youtubeContainer}>
                  <AspectRatioContainer useCssVariableSizes>
                    {/* Iframe embed from youtube, tweaked for react, but no deprecated properties removed */}
                    <iframe
                      width="560"
                      height="315"
                      src="https://www.youtube.com/embed/OC014H-ZkRw?si=kQNwPUORLLYOEu8J"
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </AspectRatioContainer>
                </div>
                {!isSubscriber && (
                  <>
                    <p>Will YOU be next?</p>
                    <p>
                      <EnterButton />
                    </p>
                  </>
                )}
              </div>
            </AboutUsSiteContainer>
          </AboutUsModule>

          <AboutUsModule background="white" withReducedPadding>
            <AboutUsSiteContainer>
              <hr className={styles.hr} />
              <img
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/winners/spotlight.png"
                alt=""
                width="104"
                height="136"
                className={styles.sectionImage}
              />
              <h2 className="aboutUsTypographyHeaderMedium">
                In the spotlight
              </h2>
              <EmblaCarousel
                className={styles.pinnedCarousel}
                items={props.pinned.map((x) =>
                  renderImageContainer(x, openModalVideo),
                )}
              />
            </AboutUsSiteContainer>
          </AboutUsModule>
          <AboutUsModule background="white" withReducedPadding>
            <AboutUsSiteContainer>
              <hr className={styles.hr} />
              <TerribleUxPaging
                items={props.spotlight}
                itemRenderer={(x) => renderImageContainer(x, openModalVideo)}
              />
            </AboutUsSiteContainer>
          </AboutUsModule>

          <AboutUsModule background="white" withReducedPadding>
            <AboutUsSiteContainer>
              <hr className={styles.hr} />
              <img
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/winners/star.png"
                alt=""
                width="118"
                height="114"
                className={styles.sectionImage}
              />
              <h2 className="aboutUsTypographyHeaderMedium">
                The wall of fame
              </h2>
              <p className="textAlignCenter">
                A few more faces making up the DAYMADE #winningfam
              </p>
              <EmblaCarousel
                className={styles.wallOfFameCarousel}
                emblaCarouselInfo={wallOfFameCarouselInfo}
                items={props.wallOfFame
                  .reduce(
                    (acc, x, index) => {
                      if (index % 2 === 0) {
                        acc.push([x, props.wallOfFame[index + 1]]);
                      }
                      return acc;
                    },
                    [] as (typeof props.wallOfFame)[],
                  )
                  .map((x, index) => (
                    <div
                      className={styles.wallOfFameImagePairContainer}
                      key={index}
                    >
                      <div className={styles.wallOfFameImageContainer}>
                        <img
                          src={resizePlasmicImage(x[0].photoSquare.url)}
                          alt={x[0].title}
                        />
                      </div>
                      {x[1] && (
                        <div className={styles.wallOfFameImageContainer}>
                          <img
                            src={resizePlasmicImage(x[1].photoSquare.url)}
                            alt={x[1].title}
                          />
                        </div>
                      )}
                    </div>
                  ))}
              >
                <CarouselButtons
                  changeSelectedIndex={(x) =>
                    x < 0
                      ? wallOfFameCarouselInfo[1]?.scrollPrev()
                      : wallOfFameCarouselInfo[1]?.scrollNext()
                  }
                />
              </EmblaCarousel>
            </AboutUsSiteContainer>
          </AboutUsModule>

          <AboutUsModule background="tealGradient" withReducedPadding>
            <AboutUsSiteContainer>
              <h2 className="aboutUsTypographyHeaderXlarge">
                Ready to join our winners?
              </h2>
              <p>
                <EnterButton />
              </p>
            </AboutUsSiteContainer>
          </AboutUsModule>
        </div>
      </div>
    </MainLayout>
  );
};

function renderImageContainer(
  item: WinnerCmsItem,
  openModalVideo: (x: WinnerCmsItem) => void,
) {
  return (
    <div className={styles.spotlightImageContainer} key={item.photoSquare.url}>
      <img src={resizePlasmicImage(item.photoSquare.url)} alt={item.title} />
      <span
        className={clsx(
          'aboutUsTypographyHeaderMedium',
          styles.spotlightImageText,
        )}
      >
        {item.title}
      </span>
      {(item.videoLink || item.videoLinkInstagram) && (
        <button
          className={styles.playButton}
          onClick={() => openModalVideo(item)}
        >
          <img
            src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/winners/VideoPlay.png"
            alt="Play winner video"
          />
        </button>
      )}
    </div>
  );
}

interface TerribleUxPagingProps<T> {
  items: T[];
  itemRenderer: (item: T) => ReactElement;
}

/** A component that implements the awful paging behaviour of the old winners page in plasmic. It has its own
 * wonky semantics, like showing 9 items per page on desktop, and 5 items per page on mobile. There is no
 * guarantee that a single page will fit on the screen, especially on mobile, so the UI has to scroll to the
 * top when changing pages
 */
const TerribleUxPaging = <T,>(props: TerribleUxPagingProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const isMobile = useIsMobile().isMobile;
  const pageSize = isMobile ? 5 : 9;
  const pageCount = Math.max(0, Math.ceil(props.items.length / pageSize) - 1);
  const scrolling = useScrollToElement(containerRef);
  useEffect(() => {
    // If we switch from desktop sized screen to mobile, then we can end up
    // on an empty page
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  return (
    <>
      <style>
        {/* We need to adjust the offset when we scroll the HTML element because we have a fixed header. However, we can't be confident adding this rule to the global CSS won't break other
        parts of the site */}
        {`html { scroll-padding-top: calc(var(--dm2025-headerHeight) + 24px)}`}
      </style>
      <div className={styles.pagingContainer} ref={containerRef}>
        <div className={styles.pagingContainerItems}>
          {props.items
            .slice(page * pageSize, (page + 1) * pageSize)
            .map((x) => props.itemRenderer(x))}
        </div>
        <div className={styles.pagingControls}>
          <button
            className={styles.pagingPrevious}
            onClick={() =>
              setPage((x) => {
                const newPage = Math.max(0, x - 1);
                if (newPage !== x) {
                  scrolling.requestScroll();
                }
                return newPage;
              })
            }
          >
            <svg width="6" height="10" viewBox="0 0 11 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.73021 12.3435C6.85507 11.2839 6.01403 10.2663 5.17352 9.24793C3.63085 7.37882 2.08832 5.5095 0.546171 3.6397C0.179329 3.19494 0.0531962 2.66477 0.203234 2.06101C0.348412 1.47714 0.684826 1.12056 1.17495 1.00135C1.63155 0.890251 2.02186 1.06767 2.34934 1.46425C3.43305 2.77682 4.51873 4.08689 5.60205 5.39989C7.17541 7.30677 8.74746 9.21541 10.3199 11.1234C10.9445 11.8812 10.9465 12.7658 10.3209 13.5242C7.66983 16.7386 5.01902 19.9532 2.36514 23.1641C1.63155 24.0518 0.468372 23.7302 0.196054 22.5639C0.0604638 21.983 0.174207 21.4625 0.530979 21.0302C2.85772 18.2106 5.18451 15.3911 7.51213 12.5724C7.5736 12.498 7.64357 12.4339 7.73021 12.3435Z"
              />
            </svg>
          </button>
          <span className={styles.pagingPageCount}>
            Page {page + 1} of {pageCount + 1}
          </span>

          <button
            className={styles.pagingNext}
            onClick={() =>
              setPage((x) => {
                const newPage = Math.min(pageCount, x + 1);
                if (newPage !== x) {
                  scrolling.requestScroll();
                }
                return newPage;
              })
            }
          >
            <svg width="6" height="10" viewBox="0 0 11 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.73021 12.3435C6.85507 11.2839 6.01403 10.2663 5.17352 9.24793C3.63085 7.37882 2.08832 5.5095 0.546171 3.6397C0.179329 3.19494 0.0531962 2.66477 0.203234 2.06101C0.348412 1.47714 0.684826 1.12056 1.17495 1.00135C1.63155 0.890251 2.02186 1.06767 2.34934 1.46425C3.43305 2.77682 4.51873 4.08689 5.60205 5.39989C7.17541 7.30677 8.74746 9.21541 10.3199 11.1234C10.9445 11.8812 10.9465 12.7658 10.3209 13.5242C7.66983 16.7386 5.01902 19.9532 2.36514 23.1641C1.63155 24.0518 0.468372 23.7302 0.196054 22.5639C0.0604638 21.983 0.174207 21.4625 0.530979 21.0302C2.85772 18.2106 5.18451 15.3911 7.51213 12.5724C7.5736 12.498 7.64357 12.4339 7.73021 12.3435Z"
              />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

function resizePlasmicImage(url: string | undefined) {
  if (!url) {
    return undefined;
  }
  return (
    'https://img.plasmic.app/img-optimizer/v1/img?src=' +
    encodeURIComponent(url) +
    '&w=768&q=70&f=webp'
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const [pinned, spotlight, wallOfFame] = await Promise.all([
    fetchWinnersPinned(),
    fetchWinnersSpotlight(),
    fetchWinnersWallOfFame(),
  ]);

  const pinnedTrimmed = pinned;
  const spotlightWithoutPinned = spotlight.filter(
    (x) => !pinnedTrimmed.find((p) => p.photoSquare.url == x.photoSquare.url),
  );

  return {
    props: {
      pinned: pinnedTrimmed,
      spotlight: spotlightWithoutPinned,
      wallOfFame: wallOfFame.slice(
        0,
        wallOfFame.length - (wallOfFame.length % 2),
      ),
    },
  };
};

export default Winners;
